import {
  Children,
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from "react";
import { motion } from "motion/react";
import { motionVariants, type MotionVariantName } from "./motion-variants";
import { stagger as staggerTokens } from "./motion-tokens";

type MotionStaggerProps = {
  children: ReactNode;
  /** Seconds between each child's animation start. Defaults to the "base" token. */
  stagger?: number;
  /** Seconds before the first child starts. */
  delay?: number;
  className?: string;
};

/**
 * Orchestrating parent for a group of `MotionItem`s (directly, or nested
 * inside a component that forwards a `delay` prop to its own `MotionItem`).
 * Computes an explicit `index * stagger` delay per child and clones it in,
 * rather than relying on Motion's `staggerChildren` context propagation
 * (which requires children to *not* own their `initial`/`animate` props --
 * `MotionItem` always does, so it can also animate standalone).
 */
export const MotionStagger = ({
  children,
  stagger = staggerTokens.base,
  delay = 0,
  className,
}: MotionStaggerProps) => (
  <div className={className}>
    {Children.map(children, (child, index) => {
      if (!isValidElement(child)) return child;
      return cloneElement(child as ReactElement<{ delay?: number }>, {
        delay: delay + index * stagger,
      });
    })}
  </div>
);

type MotionItemProps = {
  children: ReactNode;
  /** Name of a variant registered in motion-variants.ts. */
  variant?: MotionVariantName;
  /** Extra delay (seconds) on top of any parent-driven stagger delay. */
  delay?: number | undefined;
  className?: string;
};

/**
 * A single choreographed element. Reads its hidden/visible/exit states from
 * the shared variant registry so every screen animates with the same
 * vocabulary. Always owns its own `initial`/`animate`/`exit`, so it works
 * standalone or as a child of `MotionStagger` (which injects a computed
 * `delay`).
 */
export const MotionItem = ({
  children,
  variant = "fadeUp",
  delay,
  className,
}: MotionItemProps) => {
  const baseVariants = motionVariants[variant];
  const variants = delay
    ? {
        ...baseVariants,
        visible: {
          ...baseVariants.visible,
          transition: {
            // @ts-expect-error -- transition shape varies per variant, spread is safe here
            ...baseVariants.visible.transition,
            delay,
          },
        },
      }
    : baseVariants;

  return (
    <motion.div
      className={className}
      variants={variants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      {children}
    </motion.div>
  );
};

type ScreenTransitionProps = {
  children: ReactNode;
  className?: string;
  /** Defaults to the generic screen fade; override for bespoke screens like the splash. */
  variant?: MotionVariantName;
};

/**
 * Wrapper for root-level screens (splash, home, onboarding...). Use inside
 * `AnimatePresence` with a stable `key` so screens can exit before the next
 * one mounts.
 */
export const ScreenTransition = ({
  children,
  className,
  variant = "screenFade",
}: ScreenTransitionProps) => (
  <motion.div
    className={className}
    variants={motionVariants[variant]}
    initial="hidden"
    animate="visible"
    exit="exit"
  >
    {children}
  </motion.div>
);
